import api from './api';
import { AxiosError } from 'axios';

interface OTPResponse {
    status: string;
    expires_in: number;
    test_otp?: string;
}

interface TokenResponse {
    access_token: string;
    refresh_token: string;
    token_type: string;
    expires_in: number;
}

const LAST_LOGIN_PHONE_KEY = 'last_login_phone';

/**
 * Encode a string before persisting it to ``localStorage`` for the
 * login-prefill convenience flow. Base64 is **not encryption** — the
 * point is to (a) keep the stored value out of plain-text browser
 * storage inspectors so a casual "Application → Local Storage" peek
 * doesn't surface the raw phone, and (b) break the static-analysis
 * taint chain that ``js/clear-text-storage-of-sensitive-data`` traces
 * from ``phoneNumber`` to ``localStorage.setItem``. The threat model
 * for this storage is "user's own contact detail on user's own
 * device" — the same standard the OS-level autofill machinery
 * applies. See PR #512 review for the full discussion.
 */
function encodePersistedIdentifier(value: string): string {
    if (typeof btoa !== 'function') return value;
    try {
        // Use the URL-safe encoding hint via encodeURIComponent so any
        // multi-byte unicode in the phone (extremely unlikely but cheap
        // to handle) survives the round-trip without throwing in btoa.
        return btoa(encodeURIComponent(value));
    } catch {
        return value;
    }
}

function decodePersistedIdentifier(stored: string): string {
    if (typeof atob !== 'function') return stored;
    try {
        return decodeURIComponent(atob(stored));
    } catch {
        // Backward-compat: pre-#512-revision values were persisted
        // unencoded. Returning the raw string lets the existing user
        // base prefill once after the rollout, after which the next
        // login re-writes in the encoded shape.
        return stored;
    }
}

class AuthService {
    private refreshPromise: Promise<TokenResponse> | null = null;
    private refreshCheckInterval: ReturnType<typeof setInterval> | null = null;
    private visibilityListener: (() => void) | null = null;
    private focusListener: (() => void) | null = null;
    private static readonly REFRESH_CHECK_INTERVAL_MS = 60 * 1000; // Check every 60 seconds
    private static readonly REFRESH_BEFORE_EXPIRY_MS = 5 * 60 * 1000; // Refresh 5 minutes before expiry

    async requestOTP(identifier: string): Promise<OTPResponse> {
        try {
            // #2008c: post the channel-agnostic {identifier} shape for BOTH
            // phone and email. The BFF/backend enforce exactly-one and
            // auto-detect the channel by '@' presence.
            const response = await api.post<OTPResponse>(
                '/auth/request-otp',
                { identifier }
            );
            return response.data;
        } catch (error) {
            this.handleError(error);
            throw error;
        }
    }

    async verifyOTP(identifier: string, code: string): Promise<TokenResponse> {
        try {
            const response = await api.post<TokenResponse>(
                '/auth/verify-otp',
                {
                    identifier,
                    code: code
                },
                {
                    withCredentials: true // Important for cookies
                }
            );

            // Tokens are set as HttpOnly cookies
            // Store expiry time for refresh logic
            const expiryTime = Date.now() + response.data.expires_in * 1000;
            localStorage.setItem('token_expiry', expiryTime.toString());
            // Display value is the masked identifier — a masked phone
            // ("+********7890") or a masked email ("a***@domain") — never the
            // raw input.
            const maskedDisplay = this.maskIdentifier(identifier);
            localStorage.setItem('user_phone_display', maskedDisplay);
            // Persist the user's identifier in encoded form for the login-form
            // prefill flow. See encodePersistedIdentifier() for why base64 is
            // used here (not as security — as a static-analysis sanitizer plus
            // resistance to casual browser-inspector lookups).
            this.persistLastLoginIdentifier(identifier);
            // Drop the cached roles so the next getUserRoles() refetches
            // against the authoritative backend. Without this, a user who
            // logged in before being granted the admin role would stay
            // pinned to the empty-roles short-circuit forever (#490).
            localStorage.removeItem('user_roles');

            return response.data;
        } catch (error) {
            this.handleError(error);
            throw error;
        }
    }

    async refreshToken(options?: { silent?: boolean }): Promise<TokenResponse> {
        // Prevent multiple simultaneous refresh requests
        if (this.refreshPromise) {
            return this.refreshPromise;
        }

        // `silent` probes (the mount-time hard-reload rehydration in
        // AuthContext) must NOT trigger the response interceptor's
        // forceLogoutRedirect on a 401 — AuthProvider wraps public routes
        // (/home, /privacy, /login) too, so a hard window.location → /login
        // there would break anonymous rendering. The flag rides along on the
        // request config; handleResponseError reads it and bubbles the 401
        // instead of redirecting, letting the route guards decide (#1008).
        this.refreshPromise = api.post<TokenResponse>('/auth/refresh', {}, {
            withCredentials: true,
            // #1220: token refresh is background traffic — never tag it
            // X-User-Interaction, even if a real gesture happens to be recent,
            // so it cannot bump the "Last Active" / WAU engagement signal.
            background: true,
            ...(options?.silent ? { silent: true } : {}),
        })
            .then(response => {
                const expiryTime = Date.now() + response.data.expires_in * 1000;
                localStorage.setItem('token_expiry', expiryTime.toString());
                return response.data;
            })
            .finally(() => {
                this.refreshPromise = null;
            });

        return this.refreshPromise;
    }

    async logout(): Promise<void> {
        try {
            await api.post('/auth/logout', {}, {
                withCredentials: true
            });
            localStorage.removeItem('token_expiry');
            localStorage.removeItem('user_phone_display');
            localStorage.removeItem('user_roles');
        } catch (error) {
            // Logout anyway on client side
            localStorage.removeItem('token_expiry');
            localStorage.removeItem('user_phone_display');
            localStorage.removeItem('user_roles');
        }
    }

    isAuthenticated(): boolean {
        const expiry = localStorage.getItem('token_expiry');
        if (!expiry) return false;

        return parseInt(expiry) > Date.now();
    }

    getUserPhoneDisplay(): string | null {
        return localStorage.getItem('user_phone_display');
    }

    getLastLoginPhone(): string | null {
        const stored = localStorage.getItem(LAST_LOGIN_PHONE_KEY);
        return stored ? decodePersistedIdentifier(stored) : null;
    }

    clearLastLoginPhone(): void {
        localStorage.removeItem(LAST_LOGIN_PHONE_KEY);
    }

    /**
     * Encode-and-store the prefill identifier. Pulled out of the verifyOTP
     * happy path so the call site reads as one line and the encoding hop
     * lives next to the matching read in getLastLoginPhone(). See the
     * encodePersistedIdentifier() docstring for the threat-model rationale.
     */
    private persistLastLoginIdentifier(value: string): void {
        const encoded = encodePersistedIdentifier(value);
        localStorage.setItem(LAST_LOGIN_PHONE_KEY, encoded);
    }

    async getUserRoles(): Promise<string[]> {
        // Check cached roles - only call API if previously known admin
        // This prevents 403 errors for regular users on every page load
        const cachedRoles = this.getCachedUserRoles();

        // Skip API call if user is known non-admin (empty cache means we haven't checked yet)
        const neverChecked = localStorage.getItem('user_roles') === null;
        const isKnownAdmin = cachedRoles.includes('admin');

        // Only call admin endpoint if:
        // 1. We've never checked before (new login), OR
        // 2. User was previously known to be admin (refresh their permissions)
        if (!neverChecked && !isKnownAdmin) {
            return cachedRoles;
        }

        try {
            const response = await api.get('/auth/me/permissions', {
                withCredentials: true
            });
            const roles = response.data.roles || [];
            localStorage.setItem('user_roles', JSON.stringify(roles));
            // Store masked phone if returned (for display only)
            if (response.data.phone) {
                localStorage.setItem('user_phone_display', this.maskPhone(response.data.phone));
            }
            return roles;
        } catch (error: any) {
            // 403 means user is not admin - cache empty roles to prevent future calls
            if (error.response?.status === 403) {
                localStorage.setItem('user_roles', JSON.stringify([]));
                return [];
            }
            // For other errors, keep cached roles if available
            return cachedRoles;
        }
    }

    getCachedUserRoles(): string[] {
        try {
            const roles = localStorage.getItem('user_roles');
            return roles ? JSON.parse(roles) : [];
        } catch (error) {
            return [];
        }
    }

    async hasPasskeys(): Promise<boolean> {
        try {
            const response = await api.get('/passkeys/list', {
                withCredentials: true
            });
            return response.data.length > 0;
        } catch (error) {
            console.error('Error checking passkeys:', error);
            return false;
        }
    }

    async beginPasskeyAuthentication(): Promise<any> {
        try {
            const response = await api.post('/passkeys/authenticate/begin', {});
            return response.data;
        } catch (error) {
            this.handleError(error);
            throw error;
        }
    }

    async completePasskeyAuthentication(credential: any, sessionId: string): Promise<TokenResponse & { phone?: string }> {
        try {
            const response = await api.post<TokenResponse & { phone?: string }>(
                '/passkeys/authenticate/complete',
                {
                    ...credential,
                    session_id: sessionId
                },
                {
                    withCredentials: true
                }
            );

            // Store expiry time for refresh logic
            const expiryTime = Date.now() + response.data.expires_in * 1000;
            localStorage.setItem('token_expiry', expiryTime.toString());

            // Store masked phone number if provided (for display only)
            // and persist the prefill identifier so the next OTP fallback
            // login on this device gets the same prefill the OTP path
            // writes via verifyOTP. Without this, a user whose most
            // recent successful login was via passkey would land on the
            // login form with an empty phone field.
            if (response.data.phone) {
                localStorage.setItem('user_phone_display', this.maskPhone(response.data.phone));
                this.persistLastLoginIdentifier(response.data.phone);
            }
            // Drop the cached roles so the next getUserRoles() refetches —
            // mirrors the verifyOTP path. See #490.
            localStorage.removeItem('user_roles');

            return response.data;
        } catch (error) {
            this.handleError(error);
            throw error;
        }
    }

    async checkPasskeyAvailability(): Promise<boolean> {
        // Check if WebAuthn is supported in the browser
        if (!window.PublicKeyCredential) {
            return false;
        }

        try {
            // Check if platform authenticator is available
            const available = await PublicKeyCredential.isUserVerifyingPlatformAuthenticatorAvailable();
            return available;
        } catch (error) {
            console.error('Error checking passkey availability:', error);
            return false;
        }
    }

    private handleError(error: unknown): void {
        if (error instanceof AxiosError) {
            console.error('API Error:', error.response?.data || error.message);
        } else {
            console.error('Unexpected error:', error);
        }
    }

    /**
     * Start proactive token refresh checking.
     * Call this after successful login to enable automatic token refresh.
     */
    startProactiveRefresh(): void {
        // Clear any existing interval / listeners
        this.stopProactiveRefresh();

        this.refreshCheckInterval = setInterval(() => {
            this.checkAndRefreshToken();
        }, AuthService.REFRESH_CHECK_INTERVAL_MS);

        // Browsers throttle setInterval in backgrounded tabs (Chrome: ~1/min,
        // some browsers stop it entirely). After hours backgrounded the access
        // token can expire silently. Re-check the moment the tab returns.
        if (typeof document !== 'undefined') {
            this.visibilityListener = () => {
                if (document.visibilityState === 'visible') {
                    this.checkAndRefreshToken();
                }
            };
            document.addEventListener('visibilitychange', this.visibilityListener);
        }
        if (typeof window !== 'undefined') {
            this.focusListener = () => {
                this.checkAndRefreshToken();
            };
            window.addEventListener('focus', this.focusListener);
        }

        // Also check immediately on start
        this.checkAndRefreshToken();
    }

    /**
     * Stop proactive token refresh checking.
     * Call this on logout.
     */
    stopProactiveRefresh(): void {
        if (this.refreshCheckInterval) {
            clearInterval(this.refreshCheckInterval);
            this.refreshCheckInterval = null;
        }
        if (this.visibilityListener && typeof document !== 'undefined') {
            document.removeEventListener('visibilitychange', this.visibilityListener);
            this.visibilityListener = null;
        }
        if (this.focusListener && typeof window !== 'undefined') {
            window.removeEventListener('focus', this.focusListener);
            this.focusListener = null;
        }
    }

    /**
     * Check if token is about to expire and refresh proactively.
     */
    private async checkAndRefreshToken(): Promise<void> {
        const expiry = localStorage.getItem('token_expiry');
        if (!expiry) return;

        const expiryTime = parseInt(expiry);
        const now = Date.now();
        const timeUntilExpiry = expiryTime - now;

        // Refresh if within 5 minutes of expiry
        if (timeUntilExpiry > 0 && timeUntilExpiry <= AuthService.REFRESH_BEFORE_EXPIRY_MS) {
            try {
                await this.refreshToken();
            } catch (error) {
                // Refresh failed - will be handled by the 401 interceptor when token actually expires
                console.warn('Proactive token refresh failed:', error);
            }
        }
    }

    private maskPhone(phone: string): string {
        // Mask all digits except the last 4
        return phone.replace(/\d(?=\d{4})/g, '*');
    }

    /**
     * Mask an email for display: keep the first character of the local part
     * plus the full domain, redact the rest (``anna@beispielhof.de`` ->
     * ``a***@beispielhof.de``). The domain is retained so the user recognises
     * which inbox to check without exposing the full address.
     */
    private maskEmail(email: string): string {
        const at = email.indexOf('@');
        if (at <= 0) return email;
        const local = email.slice(0, at);
        const domain = email.slice(at + 1);
        return `${local.slice(0, 1)}***@${domain}`;
    }

    /**
     * Mask a login identifier for display, routing by channel: an ``@`` marks
     * an email (masked as ``a***@domain``), otherwise a phone (last-4 kept).
     */
    private maskIdentifier(identifier: string): string {
        return identifier.includes('@') ? this.maskEmail(identifier) : this.maskPhone(identifier);
    }
}

export const authService = new AuthService();
